import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box, Stack, styled, Typography, DialogContentText,
} from '@mui/material';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import IconButton from '@mui/material/IconButton';
import { useApp } from '../../../hooks';
import Modal from '../../../components/modals/modal';
import CustomConfirmDialog from '../custom-confirm-dialog';
import { PhotoMediaItem } from '../image-dialog';
import PhotoMetadataViewer from './photo-metadata-viewer';
import MediaInteractions from './media-interactions';

const TOP_BOTTOM_AREAS = '142px';
const IMG_TOTAL_GAP = '188.5px';

const ImageWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: '0px 40px 0px 24px',
  flex: '1 0 200px',
});

const ImageTag = styled(Box)({
  objectFit: 'contain',
  width: '100%',
  height: '100%',
  maxWidth: '100%',
  maxHeight: `calc(100vh - ${IMG_TOTAL_GAP})`,
  margin: '24px 0px 24px 0px',
});
interface Props {
  open: boolean;
  hasChanges: boolean;
  record: Record<string, any>;
  mediaItem: PhotoMediaItem;
  resetHasChanges: () => void;
  onMediaClose: () => void;
  onMediaSave: () => void;
  onMediaChange: (key: string | undefined, value: string | boolean) => void;
  handleMediaDelete: (id: string) => void;
}

const PhotoMediaViewer: React.FC<Props> = ({
  record, mediaItem, onMediaClose, onMediaSave,
  open, onMediaChange, hasChanges, resetHasChanges,
  handleMediaDelete,
}) => {
  const app = useApp();
  const { item } = mediaItem;
  const [metadataObject, setMetadataObject] = useState<Record<string, any>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDelete, setIsDelete] = useState(false);

  useEffect(() => {
    const fetchPhotoMetaData = async () => {
      await app.dataSource.getPhoto(
        item.photo_id,
        (err: any, meta: Record<string, any>) => {
          if (err) {
            setMetadataObject({});
          } else {
            setMetadataObject(meta);
          }
        },
      );
    };
    const photoIdReady = item.photo_id && item.photo_id.length > 0;

    if (app.dataSource && photoIdReady) {
      fetchPhotoMetaData()
        .catch((err) => console.warn('error fetching photo metadata', err));
    }
    return () => setMetadataObject({});
  }, [app.dataSource, item.photo_id.length]);

  const handleDeleteClick = () => {
    setIsDelete(true);
    setShowConfirmDialog(true);
  };

  const handleShareClick = async () => {
    await navigator.clipboard.writeText(metadataObject.original);
    toast('The link has been copied to the clipboard');
  };

  const handleDialogNegative = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      onMediaClose();
      setShowConfirmDialog(false);
      setIsDelete(false);
    }
  };

  const handleDialogPositive = () => {
    if (isDelete) {
      handleMediaDelete(item.photo_id);
    } else {
      setShowConfirmDialog(false);
      onMediaClose();
      resetHasChanges();
    }
  };

  const ActionTitle = () => (
    <Stack direction="row" justifyContent="start" alignItems="center">
      <IconButton
        onClick={() => {
          if (hasChanges) {
            setShowConfirmDialog(true);
          } else {
            onMediaClose();
          }
        }}
      >
        <ArrowBackTwoToneIcon />
      </IconButton>
      <Typography ml={5} sx={{ fontSize: 16, fontWeight: 700, lineWeight: '25px' }}>Media Viewer</Typography>
    </Stack>
  );

  return (
    <>
      <Modal
        open={open}
        onNegative={handleDialogNegative}
        onPositive={onMediaSave}
        positive="Save changes"
        negative="Cancel"
        title={<ActionTitle />}
        fullScreen
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ height: `calc(100vh - ${TOP_BOTTOM_AREAS})` }}
        >
          <ImageWrapper
            key="image-wrapper"
            data-testid={`test-${item.photo_id}`}
          >
            {metadataObject.large && (
              <ImageTag
                component="img"
                {...({ src: metadataObject.large, alt: item.caption } as React.ImgHTMLAttributes<HTMLImageElement>)}
              />
            )}
          </ImageWrapper>
          <Stack key="media-viewer" mt={3} mr={3} direction="column" justifyContent="space-between" alignItems="start">
            {/* MediaInteractions - placeholder component */}
            <MediaInteractions
              key="media-interactions"
              metadata={metadataObject}
              handleDeleteClick={handleDeleteClick}
              handleShareClick={handleShareClick}
            />
            <PhotoMetadataViewer
              key="media-metadata"
              record={record}
              photoMetadata={metadataObject}
              onChange={onMediaChange}
              photoMediaItem={mediaItem}
            />
          </Stack>
        </Stack>
      </Modal>
      <CustomConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title={isDelete ? 'Delete Media' : 'Discard Changes'}
        positive={isDelete ? 'Delete Media' : 'Discard Changes'}
        onNegative={() => {
          setShowConfirmDialog(false);
          setIsDelete(false);
        }}
        onPositive={handleDialogPositive}
      >
        <DialogContentText component="span" data-testid="dialog-content">
          <Typography variant="body1">
            {
              isDelete
                ? 'Are you sure you want to delete this media file ?'
                : 'This section has unsaved changes. If you discard now, all the changes will be lost.'
            }
          </Typography>
        </DialogContentText>
      </CustomConfirmDialog>
    </>
  );
};
export default PhotoMediaViewer;
